import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GET_GROUPS, GET_CONVERSATION_COUNTS_BY_GROUPS, GET_CONVERSATION_COUNT_BY_GROUP } from '@zaplify/graphql';
import { useQuery, useApolloClient } from '@apollo/client';
import { cn } from '@shadcn/ui/lib/utils';
import { AnimatePresence } from 'motion/react';
import { ConversationCardSkeleton } from '../../../components/conversation-card-skeleton';
import { ContactConversationCard } from './contact-conversation-card';
import { Input } from '@shadcn/ui/components/ui/input';
import { Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { PlaybookFilterBadge } from '../../../components/playbook-filter-badge';
import { usePaginatedConversations } from '../hooks/use-paginated-conversations';

interface ContactConversationsListProps {}

export const ContactConversationsList: React.FC<ContactConversationsListProps> = () => {
    const listContainerRef = useRef<HTMLDivElement>(null);
    const [query, setQuery] = useState('');
    const [searchParams] = useSearchParams();
    const searchParamGroupId = searchParams.get('groupId');
    const apolloClient = useApolloClient();

    // Get available groups from GET_GROUPS query to match against
    const { data: availableGroupsData } = useQuery(GET_GROUPS);
    const availableGroupIds = useMemo(
        () => new Set(availableGroupsData?.Groups?.map((g) => g.id) || []),
        [availableGroupsData]
    );

    // Build the where clause for filtering conversations
    const whereClause = useMemo(() => {
        const conditions: any = {
            // Only include conversations that have at least one message
            Messages: { sentOn: { _is_null: false } },
        };

        // Group filtering through Contact relationship
        if (searchParamGroupId) {
            if (searchParamGroupId === 'unassigned') {
                // Filter for contacts with no valid groups (either no groups or groups that don't exist in GET_GROUPS)
                if (availableGroupIds.size > 0) {
                    conditions.Contact = {
                        _not: {
                            _GroupToContacts: {
                                Group: { id: { _in: Array.from(availableGroupIds) } },
                            },
                        },
                    };
                } else {
                    // Fallback if no groups are available
                    conditions.Contact = {
                        _not: {
                            _GroupToContacts: {
                                Group: { id: { _is_null: false } },
                            },
                        },
                    };
                }
            } else {
                // Only filter by group ID if it exists in available groups
                if (availableGroupIds.has(searchParamGroupId)) {
                    conditions.Contact = {
                        _GroupToContacts: {
                            Group: { id: { _eq: searchParamGroupId } },
                        },
                    };
                } else {
                    // If the group doesn't exist, return no results
                    conditions.id = { _eq: 'non-existent-id' };
                }
            }
        }

        // Search query filtering through Contact relationship
        if (query.length > 1) {
            const searchConditions = {
                _or: [
                    { fullName: { _ilike: `%${query}%` } },
                    { organizationName: { _ilike: `%${query}%` } },
                    { email: { _ilike: `%${query}%` } },
                ],
            };

            // Combine with existing Contact conditions if they exist
            if (conditions.Contact) {
                conditions.Contact = {
                    _and: [conditions.Contact, searchConditions],
                };
            } else {
                conditions.Contact = searchConditions;
            }
        }

        return conditions;
    }, [query, searchParamGroupId, availableGroupIds]);

    // -----------------------------------------------------------------------
    // Conversations data (paginated)
    // -----------------------------------------------------------------------
    const {
        conversations,
        isInitialLoading: loading,
        hasMore,
        isFetchingMore,
        loadMore,
    } = usePaginatedConversations(whereClause, 25);

    // Get conversation counts for group filtering using optimized count queries
    const { data: conversationCountsData } = useQuery(GET_CONVERSATION_COUNTS_BY_GROUPS, {
        variables: { availableGroupIds: Array.from(availableGroupIds) },
        fetchPolicy: 'cache-and-network',
    });

    // Create a state to store individual group counts
    const [individualGroupCounts, setIndividualGroupCounts] = useState<Record<string, number>>({});

    // Fetch individual group counts for all available groups
    useEffect(() => {
        const fetchGroupCounts = async () => {
            const groupIdsArray = Array.from(availableGroupIds);
            if (groupIdsArray.length === 0) return;

            // We'll use Promise.all to fetch all group counts in parallel
            const countPromises = groupIdsArray.map(async (groupId) => {
                try {
                    const result = await apolloClient.query({
                        query: GET_CONVERSATION_COUNT_BY_GROUP,
                        variables: { groupId },
                        fetchPolicy: 'cache-first',
                    });
                    return {
                        groupId,
                        count: result?.data?.groupCount?.aggregate?.count || 0,
                    };
                } catch (error) {
                    console.error(`Error fetching count for group ${groupId}:`, error);
                    return { groupId, count: 0 };
                }
            });

            try {
                const results = await Promise.all(countPromises);
                const newCounts: Record<string, number> = {};
                results.forEach(({ groupId, count }) => {
                    newCounts[groupId] = count;
                });
                setIndividualGroupCounts(newCounts);
            } catch (error) {
                console.error('Error fetching group counts:', error);
            }
        };

        fetchGroupCounts();
    }, [availableGroupIds, apolloClient]);

    const groupCounts = useMemo(() => {
        // Use the optimized count data directly from the server
        const totalCount = conversationCountsData?.totalCount?.aggregate?.count || 0;
        const unassignedCount = conversationCountsData?.unassignedCount?.aggregate?.count || 0;

        // Use the individual group counts from the custom hook
        const groupCounts = individualGroupCounts;

        const result = {
            totalCount,
            groupCounts,
            unassignedCount,
        };

        return result;
    }, [conversationCountsData, individualGroupCounts]);

    // -----------------------------------------------------------------------
    // Intersection observer for infinite scroll (simpler & more reliable)
    // -----------------------------------------------------------------------
    const observer = useRef<IntersectionObserver | null>(null);

    const lastItemRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (isFetchingMore) return;

            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMore) {
                        loadMore();
                    }
                },
                {
                    root: listContainerRef.current,
                    rootMargin: '0px',
                    threshold: 0.1,
                }
            );

            if (node) observer.current.observe(node);
        },
        [isFetchingMore, hasMore, loadMore]
    );

    // Scroll to top when filters change
    useEffect(() => {
        if (listContainerRef.current) {
            listContainerRef.current.scrollTop = 0;
        }
    }, [whereClause]);

    return (
        <div className={cn('flex flex-col w-full md:max-w-xs mx-auto h-full max-h-full overflow-hidden border-r')}>
            <header className="sticky top-0 w-full min-w-full h-14 border-b bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/60">
                <div
                    className="chats-title flex items-center gap-4 p-3" // @IMPORTANT Needed for frigade tour (classname must be exact)
                >
                    <span className={`font-medium text-2xl`}>All conversations</span>
                </div>
            </header>

            <div className="flex gap-2 items-center py-2 px-4 border-b">
                <Search className="text-text-tertiary w-5" />
                <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search conversations"
                    className="border-none ring-0 shadow-none focus-visible:ring-0"
                />
                <PlaybookFilterBadge
                    size="sm"
                    counts={{ totalCount: groupCounts.totalCount, groupCounts: groupCounts.groupCounts }}
                    extraCategories={[
                        { id: 'unassigned', name: 'Unassigned', imgSrc: undefined, count: groupCounts.unassignedCount },
                    ]}
                />
            </div>
            <div className="flex flex-col max-h-full p-2 w-full gap-1 overflow-y-auto" ref={listContainerRef}>
                {loading ? (
                    <>
                        <ConversationCardSkeleton key={'contact-skeleton-0'} withBadge={false} />
                        <ConversationCardSkeleton key={'contact-skeleton-1'} withBadge={false} />
                        <ConversationCardSkeleton key={'contact-skeleton-2'} withBadge={false} />
                        <ConversationCardSkeleton key={'contact-skeleton-3'} withBadge={false} />
                        <ConversationCardSkeleton key={'contact-skeleton-4'} withBadge={false} />
                    </>
                ) : conversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-center">
                        <p className="text-sm text-gray-500">No conversations</p>
                        <p className="text-xs text-gray-400 mt-1">
                            {query || searchParamGroupId
                                ? 'Try adjusting your search or filter'
                                : 'Start a conversation to see it here'}
                        </p>
                    </div>
                ) : (
                    <AnimatePresence>
                        <div className="flex flex-col w-full gap-1">
                            <AnimatePresence>
                                {conversations
                                    .filter((conversation) => conversation.Contact) // Only show conversations with contacts
                                    .map((conversation, index) => {
                                        const isLast = index === conversations.length - 1;
                                        return (
                                            <div
                                                key={`conversation-${conversation.id}`}
                                                data-conversation-id={conversation.id}
                                                ref={isLast ? lastItemRef : undefined}
                                            >
                                                <ContactConversationCard
                                                    contact={{
                                                        ...conversation.Contact,
                                                        Messages: conversation.Messages,
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                            </AnimatePresence>
                        </div>
                    </AnimatePresence>
                )}
                {isFetchingMore && <div className="w-full h-10 bg-gray-200 animate-pulse rounded-md mx-2" />}
            </div>
        </div>
    );
};
